import {
  Component,
  ElementRef,
  Injector,
  ViewEncapsulation,
  effect,
  forwardRef,
  inject,
  runInInjectionContext,
  input,
  output,
  viewChild,
} from "@angular/core";
import { debounceTime } from "rxjs";
import { SelectionModel } from "@angular/cdk/collections";
import { TranslateModule } from "@ngx-translate/core";
import { MatSelect, MatSelectModule } from "@angular/material/select";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { ReactiveFormsModule, FormControl, NG_VALUE_ACCESSOR } from "@angular/forms";

import { IsEnglishDirective } from "../../directives";
import { ReactiveFormsBaseComponent } from "../../base-components";
import { LocalizedNamePipe, ValidationHandlerPipe } from "../../pipes";

import { Dropdown } from "src/app/core/interfaces";

@Component({
  selector: "app-dropdown",
  standalone: true,
  imports: [ReactiveFormsModule, MatSelectModule, TranslateModule, ValidationHandlerPipe, LocalizedNamePipe, IsEnglishDirective],
  templateUrl: "./dropdown.component.html",
  styleUrl: "./dropdown.component.scss",
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
  ],
})
export class DropdownComponent extends ReactiveFormsBaseComponent {
  private injector = inject(Injector);

  readonly label = input.required<string>();
  readonly options = input.required<Dropdown[]>();

  readonly bindValue = input<keyof Dropdown>("id");
  readonly placeholder = input("ACTIONS.SEARCH");
  readonly withSearch = input(false);
  readonly multiple = input(false);
  readonly readonly = input(false);
  readonly matErrorClass = input("");

  readonly selected = output<Dropdown["id"] | Dropdown["id"][] | null>();

  readonly matMdcFormField = viewChild.required<ElementRef<HTMLElement>>("matMdcFormField");

  isEnglish = false;
  isRequiredMarkDisplayed = true;
  multipleControl = new FormControl<Dropdown["id"][]>([]);

  filterControl = new FormControl<string | null>(null);
  filteredOptions: Dropdown[] = [];
  filteredGroupsOptions: { name: string; options: Dropdown[] }[] = [];

  selection = new SelectionModel<Dropdown["id"]>(true, []);

  private originalGroupsOptions: { name: string; options: Dropdown[] }[] = [];

  get isHaveGroupBy() {
    return this.options()?.find((option) => option.groupBy) !== undefined;
  }

  get isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.options().length;
    return numSelected === numRows;
  }

  constructor() {
    super();

    const filterControlValue = toSignal(this.filterControl.valueChanges.pipe(debounceTime(500), takeUntilDestroyed()));

    effect(() => {
      if (this.options()) this.filteredOptions = this.options();

      if (this.withSearch()) {
        const searchKey = filterControlValue();

        if (searchKey) {
          if (this.isHaveGroupBy) {
            this.filterGroupsOptions(searchKey);
          } else {
            if (!searchKey?.length) {
              this.filteredOptions = this.options();
              return;
            }

            this.filteredOptions = this.options().filter(
              (each) =>
                each.nameEn.toLowerCase().includes(searchKey.toLowerCase()) || each.nameAr.toLowerCase().includes(searchKey.toLowerCase()),
            );
          }
        }
      }
    });
  }

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.

    this.multipleControl.setValue(this.value);
    this.multipleControl.setValidators(this.control.validator);
    this.multipleControl.updateValueAndValidity();
  }

  ngAfterViewInit(): void {
    //Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
    //Add 'implements AfterViewInit' to the class.

    this.isRequiredMarkDisplayed = !!this.matMdcFormField().nativeElement.querySelector(".mat-mdc-form-field-required-marker");
  }

  ngAfterContentInit(): void {
    //Called after ngOnInit when the component's or directive's content has been initialized.
    //Add 'implements AfterContentInit' to the class.

    if (this.isHaveGroupBy) {
      this.generateGroupsOptions();
      this.filteredGroupsOptions = this.originalGroupsOptions;
    }

    runInInjectionContext(this.injector, () => {
      const controlValueSignal = toSignal(this.control.valueChanges);

      effect(() => {
        if (controlValueSignal() && !this.multipleControl.value?.length && this.multiple()) {
          this.multipleControl.setValue(controlValueSignal());
        }
      });
    });
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  toggleAllRows() {
    if (this.isAllSelected) {
      this.selection.clear();
      return;
    }

    this.selection.select(...(this.options().map((option) => option[this.bindValue()]) as Dropdown["id"][]));
  }

  getSelectedValue(event: { source: MatSelect; value: Dropdown["id"] | Dropdown["id"][] | null }) {
    if (this.multiple() && Array.isArray(this.selection.selected)) {
      let finalValue = this.selection.selected;

      if (this.isAllSelected) {
        finalValue = ["all", ...finalValue];
      } else {
        finalValue = finalValue.filter((value) => value !== "all");
      }

      this.selected.emit(this.selection.selected);
      this.control.setValue(this.selection.selected);
      this.multipleControl.setValue(finalValue);
      return;
    }

    this.selected.emit(event.value);
  }

  /**
   *
   * @description using `reduce` method to generate new array contains non duplicated `groupBy` texts.
   * @description check if the `previousValue` don't have the `groupBy` text then push it to the `previousValue` array if the condition is `true`
   * @description return an array according to `groupsOptions`'s interface.
   */
  private generateGroupsOptions() {
    this.originalGroupsOptions = this.options()
      .reduce((previousValue: string[], currentValue) => {
        // check if the `previousValue` don't have the `groupBy` text then push it to the `previousValue` array if the condition is `true`
        if (!previousValue.includes(currentValue.groupBy as string)) previousValue.push(currentValue.groupBy as string);

        return previousValue;
      }, [])
      .map((groupBy) => ({
        name: groupBy as string,
        options: this.options().filter((option) => option.groupBy === groupBy),
      }));
  }

  /**
   *
   * @param searchKey: `string | null`
   * @description filter in `groupsOptions` array based on `searchKey` value.
   */
  private filterGroupsOptions(searchKey: string | null) {
    this.generateGroupsOptions();

    if (!searchKey?.length) {
      this.filteredGroupsOptions = this.originalGroupsOptions;
      return;
    }

    this.filteredGroupsOptions = this.originalGroupsOptions
      .filter((each) => {
        each.options = each.options.filter(
          (option) =>
            option.nameEn.toLowerCase().includes(searchKey.toLowerCase()) || option.nameAr.toLowerCase().includes(searchKey.toLowerCase()),
        );
        return each.options;
      })
      .filter((each) => each.options.length);
  }

  compareWithFn = (valueOption: Dropdown, dropdownOption: Dropdown) => {
    const bindValue = this.bindValue();
    const HAS_ID = valueOption?.[bindValue] && dropdownOption?.[bindValue];

    return HAS_ID ? valueOption[this.bindValue()] === dropdownOption[this.bindValue()] : valueOption == dropdownOption;
  };
}
